import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { css } from '@styled-system/css'
import { variant } from 'styled-system'
import { Button, Flex, ResponsiveSVG } from 'ui/acro'
import { Box, P } from 'ui/bend'
import CloseIcon from 'ui/acro/blocks/ContentModal/CloseIcon'
import { CSSTransition } from 'react-transition-group'

const PopUpModalBox = styled(Box)(() =>
  css({
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    width: '100%',
    height: '100%'
  })
)

const PopUp = styled(Flex)(
  () =>
    css({
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: 'white',
      boxShadow: `3px 2px 20px 0px #4a4a4a9c`,
      padding: '20px',
      color: 'black',
      height: '16em',
      width: ['90%', '100%'],
      zIndex: '102',
      position: 'fixed',
      left: '50%',
      transform: 'translateX(-50%)',
      top: ['10%', null, null, '25%']
    }),
  variant({
    variants: {
      feedFM: {
        height: 'auto',
        borderRadius: '10px',
        maxWidth: '35em',
        padding: '0px'
      },
      promoSignIn: {
        padding: '0px',
        top: ['10%', null, null, '12%'],
        height: ['44em'],
        borderRadius: '0',
        maxWidth: ['36em']
      }
    }
  })
)

const Overlay = styled(Box)(
  () =>
    css({
      backgroundColor: '#242424',
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: '0',
      right: '0',
      left: '0',
      bottom: '0',
      opacity: '.75',
      zIndex: '101'
    }),
  variant({
    variants: {
      feedFM: {
        position: 'fixed'
      },
      promoSignIn: {
        position: 'fixed'
      }
    }
  })
)

const ConditionalText = styled(P)(() =>
  css({
    textAlign: ['center', 'left'],
    fontFamily: 'Arquitecta,Helvetica,Arial,sans-serif',
    textTransform: 'uppercase',
    fontSize: '17px',
    color: '#000',
    letterSpacing: '2.5px',
    fontWeight: '500'
  })
)

const TitleContainer = styled(Flex)(
  () =>
    css({
      width: '100%',
      justifyContent: 'space-between',
      alignItems: 'flex-start'
    }),
  variant({
    variants: {
      feedFM: {
        background: '#F2F2F2',
        borderRadius: '10px 10px 0px 0px',
        justifyContent: 'center',
        flexDirection: 'column-reverse'
      }
    }
  })
)

const ContentContainer = styled(Box)(
  () =>
    css({
      width: 'auto'
    }),
  variant({
    variants: {
      promoSignIn: {
        width: '100%',
        marginTop: ['-34px'] //avoid signIn margin
      }
    }
  })
)

const HeaderText = styled(Flex)(
  () =>
    css({
      fontFamily: 'Arquitecta',
      fontWeight: '700',
      textTransform: 'uppercase'
    }),
  variant({
    variants: {
      feedFM: {
        color: 'black',
        fontWeight: '950',
        fontSize: ['35px', '44px'],
        lineHeight: '50px',
        width: '55%',
        margin: '30px 0 50px 0',
        textAlign: 'center',
        letterSpacing: '3.5px',
        alignSelf: 'center'
      }
    }
  })
)

const CloseButtonContainer = styled(Flex)(
  () => css({}),
  variant({
    variants: {
      feedFM: {
        margin: '20px 25px auto 0',
        alignSelf: 'flex-end'
      },
      promoSignIn: {
        margin: '20px 25px auto 0',
        alignSelf: 'flex-end'
      }
    }
  })
)

const ButtonContainer = styled(Flex)(
  () =>
    css({
      width: '100%'
    }),
  variant({
    variants: {
      feedFM: {
        display: 'none'
      }
    }
  })
)

const PopUpModal = ({
  id,
  icon,
  iconColor,
  iconHeight,
  iconWidth,
  headerText,
  isOpenModal,
  setIsOpenModal,
  bodyText,
  children,
  leftButtonText,
  rightButtonText,
  leftButtonAction,
  rightButtonAction,
  styles,
  variant,
  successAction
}) => {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    setIsOpen(isOpenModal)
  }, [isOpenModal])

  const closeModal = () => {
    setIsOpen(false)
    setIsOpenModal(false)
  }

  return (
    <>
      <Box onClick={() => setIsOpen(true)} cursor='pointer'>
        {icon && (
          <ResponsiveSVG
            SVG={icon}
            fill={iconColor}
            width={iconWidth}
            height={iconHeight}
          />
        )}
      </Box>
      <CSSTransition
        in={isOpen}
        timeout={0}
        mountOnEnter
        unmountOnExit
        classNames='popup-modal'
        onEntered={() => setIsOpen(true)}
        onExited={() => closeModal()}
      >
        <PopUpModalBox id={id}>
          <PopUp style={styles && styles.modalSize} variant={variant}>
            <TitleContainer
              style={styles && styles.titleContainer}
              variant={variant}
            >
              <HeaderText style={styles && styles.title} variant={variant}>
                {headerText}
              </HeaderText>

              <CloseButtonContainer
                id='close-button'
                onClick={closeModal}
                variant={variant}
                style={styles && styles.closeButtonContainer}
              >
                <CloseIcon closeColor='#636363' />
              </CloseButtonContainer>
            </TitleContainer>

            <ContentContainer variant={variant}>
              {bodyText ? (
                <P
                  textAlign='center'
                  whiteSpace='pre-wrap'
                  style={styles && styles.bodyText}
                >
                  {bodyText}
                </P>
              ) : (
                children
              )}
            </ContentContainer>

            <ButtonContainer
              justifyContent={[
                'space-between',
                'space-between',
                'space-between',
                'space-around'
              ]}
              alignItems={['stretch', 'center']}
              flexDirection={['column', 'row']}
              style={styles && styles.btnContainer}
              variant={variant}
            >
              {successAction && (
                <ConditionalText>{successAction}</ConditionalText>
              )}
              {leftButtonText && (
                <Button
                  variant={
                    styles && styles.leftBtn
                      ? 'secondary-inverted'
                      : 'secondary'
                  }
                  border='none'
                  onClick={() => {
                    leftButtonAction ? leftButtonAction(bodyText) : closeModal()
                  }}
                  style={
                    styles ? styles.leftBtn : { textDecoration: 'underline' }
                  }
                  textTransform='capitalize'
                  padding='0 30px'
                  cursor='pointer'
                >
                  {leftButtonText}
                </Button>
              )}
              {rightButtonText && (
                <Button
                  onClick={() => {
                    closeModal()
                    rightButtonAction && rightButtonAction()
                  }}
                  style={styles && styles.rightBtn}
                >
                  {rightButtonText}
                </Button>
              )}
            </ButtonContainer>
          </PopUp>
          <Overlay onClick={closeModal} className='overlay' variant={variant} />
        </PopUpModalBox>
      </CSSTransition>
    </>
  )
}

PopUpModal.propTypes = {
  id: PropTypes.string,
  icon: PropTypes.any,
  iconColor: PropTypes.string,
  iconHeight: PropTypes.string,
  iconWidth: PropTypes.string,
  headerText: PropTypes.string,
  bodyText: PropTypes.string,
  children: PropTypes.node,
  leftButtonText: PropTypes.string,
  rightButtonText: PropTypes.string,
  leftButtonAction: PropTypes.func,
  rightButtonAction: PropTypes.func,
  isOpenModal: PropTypes.bool,
  setIsOpenModal: PropTypes.func,
  styles: PropTypes.object,
  successAction: PropTypes.string,
  variant: PropTypes.string
}

export default PopUpModal
